import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, X, FileText, Image as ImageIcon, Type, Settings2, BookOpen, Wrench, ShieldAlert, Link2, LayoutList, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth, can } from "@/lib/auth";
import { fmtDate } from "@/lib/format";
import { visibleItems, searchIndex, searchPageText, PAGE_KEYS, PAGE_NAMES, PAGE_ROUTES } from "@/lib/siteIndex";
import { PageTitle, Section, Empty } from "@/components/xd/ui";

const ICONS = { page: FileText, image: ImageIcon, text: Type, setting: Settings2, guide: BookOpen, tool: Wrench, check: ShieldAlert, link: Link2, section: LayoutList };
const GROUPS = ["home", "pages", "footer", "texts", "guides", "tools"];
const LL = { he: "עברית", ar: "العربية" };

const ResultRow = ({ it }) => {
  const { t, lang } = useLang();
  const Icon = ICONS[it.type];
  return (
    <Link to={it.to} className="xd-panel p-4 flex items-start gap-3 hover:border-[var(--xd-red)]/60 transition-colors group" data-testid={`se-result-${it.id}`}>
      <span className="h-9 w-9 shrink-0 grid place-items-center border border-white/10 bg-white/[0.03]"><Icon size={16} className="text-zinc-300" /></span>
      <span className="flex-1 min-w-0 space-y-1">
        <span className="flex flex-wrap items-center gap-2"><span className="font-medium text-sm">{it.title[lang]}</span><span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-white/10 text-zinc-400">{t(`se.type.${it.type}`)}</span></span>
        <span className="block text-xs text-zinc-400">{it.where[lang]}</span>
        <span className="block text-xs text-zinc-500">{it.desc[lang]}</span>
      </span>
      <span className="text-xs text-zinc-300 shrink-0 self-center inline-flex items-center gap-1 group-hover:text-white">{t("se.open")}<ArrowLeft size={13} /></span>
    </Link>
  );
};

const TextHit = ({ h, q }) => {
  const { t, lang } = useLang();
  return (
    <Link to={`/admin/site/pages/${h.key}?lang=${h.lang}&q=${encodeURIComponent(q)}`} className="xd-panel p-4 block hover:border-[var(--xd-red)]/60 transition-colors" data-testid={`se-text-hit-${h.key}-${h.lang}`}>
      <span className="flex flex-wrap items-center gap-2 text-sm font-medium">{PAGE_NAMES[h.key][lang]} · {LL[h.lang]}<span className="text-[10px] px-1.5 py-0.5 border border-white/10 text-zinc-400">{t("se.found_in_text")}</span></span>
      <span className="block mt-1 text-xs text-zinc-500 line-clamp-2" dir={h.lang === "ar" || h.lang === "he" ? "rtl" : "auto"}>{h.snippet}</span>
    </Link>
  );
};

const PagesPanel = ({ pages }) => {
  const { t, lang } = useLang();
  return (
    <div className="grid md:grid-cols-3 gap-3" data-testid="se-pages-panel">
      {PAGE_KEYS.map((k) => (
        <div key={k} className="xd-panel p-4 space-y-3" data-testid={`se-page-card-${k}`}>
          <div className="flex items-center justify-between gap-2"><p className="font-medium">{PAGE_NAMES[k][lang]}</p><a href={PAGE_ROUTES[k]} target="_blank" rel="noreferrer" className="text-[11px] text-zinc-500 hover:text-white ltr-num" dir="ltr">{PAGE_ROUTES[k]}</a></div>
          {["he", "ar"].map((l) => {
            const p = pages.find((x) => x.key === k && x.lang === l);
            return (
              <Link key={l} to={`/admin/site/pages/${k}?lang=${l}`} className="flex items-center justify-between gap-2 border border-white/10 px-3 py-2 text-xs hover:bg-white/5" data-testid={`se-page-edit-${k}-${l}`}>
                <span>{LL[l]}</span>
                <span className={p?.has_unpublished ? "text-amber-300" : "text-emerald-300"}>{p ? (p.has_unpublished ? t("se.unpublished") : t("se.published")) : "—"}</span>
              </Link>
            );
          })}
          {pages.filter((x) => x.key === k).map((p) => p.draft_meta && <p key={p.lang} className="text-[11px] text-zinc-500 ltr-num">{LL[p.lang]} · {t("se.modified")} {fmtDate(p.draft_meta.at)} · {p.draft_meta.by_name || p.draft_meta.by_uid}</p>)}
        </div>
      ))}
    </div>
  );
};

const Readiness = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/readiness").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  const color = { pending_legal_verification: "border-red-500/60 text-red-300", needs_review: "border-amber-500/60 text-amber-300", documented: "border-sky-600/60 text-sky-300" };
  return (
    <Section title={t("se.readiness")} testid="se-readiness">
      <p className="text-xs text-zinc-400">{t("se.readiness_intro")}</p>
      <div className="space-y-2">
        {items.map((i) => (
          <div key={i.id} className="border border-white/10 p-3 space-y-1" data-testid={`se-readiness-${i.id}`} dir="ltr">
            <div className="flex flex-wrap items-center gap-2"><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${color[i.status]}`} dir="auto">{t(`se.st.${i.status}`)}</span><span className="text-sm font-medium">{i.title}</span></div>
            <p className="text-xs text-zinc-400 leading-relaxed">{i.detail}</p>
          </div>
        ))}
      </div>
    </Section>
  );
};

export default function SiteEditor() {
  const { t } = useLang();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [pages, setPages] = useState([]);
  const inputRef = useRef(null);
  const items = useMemo(() => visibleItems(user, can), [user]);
  const allowed = user?.is_super || can(user, "content.manage") || can(user, "content.publish");
  const section = params.get("section");
  useEffect(() => { if (allowed) { api.get("/admin/pages").then((r) => setPages(r.data.items)).catch(() => setPages([])); } }, [allowed]);
  useEffect(() => { if (section) { setTimeout(() => document.querySelector(`[data-testid='se-group-${section}'], [data-testid='se-${section}']`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 200); } }, [section]);
  const results = useMemo(() => searchIndex(q, items), [q, items]);
  const hits = useMemo(() => searchPageText(q, pages), [q, pages]);
  const setQuery = (v) => { setQ(v); setParams(v ? { q: v } : {}, { replace: true }); };
  if (!allowed) return <Empty text={t("err.forbidden")} />;
  return (
    <div className="space-y-8" data-testid="site-editor-hub">
      <PageTitle eyebrow={t("admin.content")} title={t("site.title")} />
      <p className="text-sm text-zinc-400 -mt-4 max-w-3xl">{t("se.intro")}</p>
      <div className="sticky top-0 z-20 -mx-1 px-1 py-2 bg-[var(--xd-bg,#050505)]/90 backdrop-blur-md">
        <div className="relative">
          <Search size={20} className="absolute top-1/2 -translate-y-1/2 start-4 text-zinc-400 pointer-events-none" />
          <input ref={inputRef} autoFocus value={q} onChange={(e) => setQuery(e.target.value)} placeholder={t("se.search_ph")} dir="auto"
            className="w-full h-14 ps-12 pe-12 bg-black border border-white/15 focus:border-[var(--xd-red)] outline-none text-base transition-colors" data-testid="se-search-input" />
          {q && <button type="button" onClick={() => { setQuery(""); inputRef.current?.focus(); }} aria-label={t("se.clear")} className="absolute top-1/2 -translate-y-1/2 end-3 h-9 w-9 grid place-items-center text-zinc-400 hover:text-white" data-testid="se-search-clear"><X size={18} /></button>}
        </div>
      </div>
      {q.trim() ? (
        <div className="space-y-3" data-testid="se-results">
          <p className="eyebrow">{t("se.results")} · <span className="ltr-num">{results.length + hits.length}</span></p>
          {results.map((it) => <ResultRow key={it.id} it={it} />)}
          {hits.map((h) => <TextHit key={`${h.key}-${h.lang}`} h={h} q={q} />)}
          {!results.length && !hits.length && <p className="text-sm text-zinc-500" data-testid="se-no-results">{t("se.no_results")}</p>}
        </div>
      ) : (
        <div className="space-y-8">
          <Section title={t("se.g.pages")} testid="se-group-pages"><PagesPanel pages={pages} /></Section>
          {GROUPS.filter((g) => g !== "pages").map((g) => {
            const list = items.filter((i) => i.g === g);
            return list.length ? <Section key={g} title={t(`se.g.${g}`)} testid={`se-group-${g}`}><div className="grid md:grid-cols-2 gap-3">{list.map((it) => <ResultRow key={it.id} it={it} />)}</div></Section> : null;
          })}
          {user?.is_super && <Readiness />}
        </div>
      )}
    </div>
  );
}
